import { ActionIcon, Badge, Button, Group, Paper, Switch, Table, Text, TextInput, Tooltip } from '@mantine/core';
import { IconArchive, IconArchiveOff, IconPlus, IconTrash } from '@tabler/icons-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDeleteTag, useSaveTag, useTags } from '@/api/catalog';
import { useCurrentWorkspace } from '@/app/WorkspaceContext';
import { PageHeader } from '@/components/PageHeader';
import { useApiError } from '@/hooks/useApiError';

/** Tags verwalten (PRJ-10). */
export function TagsPage() {
  const { t } = useTranslation();
  const ctx = useCurrentWorkspace();
  const { data: tags = [] } = useTags(ctx.workspaceId);
  const save = useSaveTag(ctx.workspaceId);
  const remove = useDeleteTag(ctx.workspaceId);
  const onError = useApiError();
  const [name, setName] = useState('');
  const [archived, setArchived] = useState(false);
  const canCreate = ctx.isAdmin || ctx.workspace.settings.permissions.createTags === 'EVERYONE';

  const add = () => name.trim() && save.mutate({ name }, { onSuccess: () => setName(''), onError });

  return (
    <>
      <PageHeader title={t('nav.items.tags')} />
      <Group mb="md" justify="space-between">
        {canCreate ? (
          <Group gap="xs">
            <TextInput placeholder={t('tags.name')} value={name} onChange={(e) => setName(e.currentTarget.value)} onKeyDown={(e) => e.key === 'Enter' && add()} />
            <Button leftSection={<IconPlus size={16} />} onClick={add} loading={save.isPending}>
              {t('tags.create')}
            </Button>
          </Group>
        ) : (
          <span />
        )}
        <Switch label={t('projects.showArchived')} checked={archived} onChange={(e) => setArchived(e.currentTarget.checked)} />
      </Group>
      <Paper withBorder maw={720}>
        <Table highlightOnHover>
          <Table.Tbody>
            {tags
              .filter((tag) => tag.archived === archived)
              .map((tag) => (
                <Table.Tr key={tag.id}>
                  <Table.Td>
                    {ctx.isAdmin ? (
                      <TextInput
                        variant="unstyled"
                        defaultValue={tag.name}
                        onBlur={(e) => e.currentTarget.value.trim() !== tag.name && save.mutate({ id: tag.id, name: e.currentTarget.value }, { onError })}
                      />
                    ) : (
                      <Badge variant="light" color="gray" tt="none">
                        {tag.name}
                      </Badge>
                    )}
                  </Table.Td>
                  <Table.Td w={100}>
                    {ctx.isAdmin && (
                      <Group gap={4} justify="flex-end">
                        <Tooltip label={tag.archived ? t('common.restore') : t('common.archive')}>
                          <ActionIcon variant="subtle" color="gray" onClick={() => save.mutate({ id: tag.id, archived: !tag.archived }, { onError })}>
                            {tag.archived ? <IconArchiveOff size={16} /> : <IconArchive size={16} />}
                          </ActionIcon>
                        </Tooltip>
                        <Tooltip label={t('common.delete')}>
                          <ActionIcon variant="subtle" color="red" onClick={() => remove.mutate(tag.id, { onError })}>
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Tooltip>
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
          </Table.Tbody>
        </Table>
        {tags.filter((tag) => tag.archived === archived).length === 0 && (
          <Text c="dimmed" ta="center" py="md">
            {t('tags.empty')}
          </Text>
        )}
      </Paper>
    </>
  );
}
